import { DateTime } from 'luxon'

import type { ShiftBandDto, ShiftBandsResponse } from '../ajax/ConfigAjax'
import { queryClient } from '../queryClient'
import type { DoubleShift } from '../types/Shift'

export const SHIFT_BANDS_QUERY_KEY = ['config', 'shiftBands'] as const

// Note: only used if the bands have never reached the cache (a first run that starts offline). The
// letters must match appsettings' ShiftBands.
const FALLBACK_BANDS: ShiftBandDto[] = [
  { shift: 'D', shiftNumber: 1, startTime: '06:00', durationHours: 12 },
  { shift: 'N', shiftNumber: 3, startTime: '18:00', durationHours: 12 },
]

function bandLetter(band: ShiftBandDto): string {
  return (band.shift ?? '').trim().toUpperCase().charAt(0)
}

// Note: the response's timeZone is deliberately ignored. It is the Windows id ("SE Asia Standard
// Time"), which Luxon cannot parse - feeding it to a zone option yields an invalid DateTime. The
// mine zone is already Luxon's default zone, set from /Settings/timezone (an IANA id).
export function getShiftBands(): ShiftBandDto[] {
  const cached = queryClient.getQueryData<ShiftBandsResponse | null>(SHIFT_BANDS_QUERY_KEY)
  const bands = cached?.shiftBands?.length ? [...cached.shiftBands] : [...FALLBACK_BANDS]
  return bands.sort((a, b) => a.shiftNumber - b.shiftNumber)
}

// Note: day -> night -> next day's day. Afternoon is deliberately not part of the offline
// progression: the app carries shifts as DAY/NIGHT everywhere else, and an afternoon shift is
// picked up from the server on reconnect.
export function nextShiftAfter(dateIso: string, shift: string): { date: string; shift: DoubleShift } {
  if (shift === 'NIGHT') {
    return { date: DateTime.fromISO(dateIso).plus({ days: 1 }).toISODate()!, shift: 'DAY' }
  }
  return { date: dateIso, shift: 'NIGHT' }
}

function bandFor(shift: DoubleShift): ShiftBandDto | undefined {
  const letter = shift === 'DAY' ? 'D' : 'N'
  return getShiftBands().find(b => bandLetter(b) === letter)
}

// Note: the wall-clock start of a shift, from the band's configured StartTime. Resolved in Luxon's
// default zone, which the app sets to the mine's timezone.
export function shiftStart(dateIso: string, shift: DoubleShift): DateTime {
  const band = bandFor(shift)
  const [hour, minute] = (band?.startTime ?? (shift === 'DAY' ? '06:00' : '18:00')).split(':').map(Number)
  return DateTime.fromISO(dateIso).startOf('day').set({ hour: hour || 0, minute: minute || 0 })
}

export function shiftEnd(dateIso: string, shift: DoubleShift): DateTime {
  return shiftStart(dateIso, shift).plus({ hours: bandFor(shift)?.durationHours ?? 12 })
}

// Note: the clock hour a shift starts at, taken from the configured bands. The production tables had
// this hardcoded as 7 for day and 19 for night, so with bands of 06:00 and 18:00 every hour bucket
// was an hour out: the hour selector computed an index past the end of the shift and rendered no
// buttons at all, and loads went into the hour after the one they were entered in. Takes any shift
// letter, so an afternoon band is honoured rather than being treated as a night.
export function shiftStartHour(shift: string): number {
  const letter = (shift || '').trim().toUpperCase().charAt(0)
  const band = getShiftBands().find(b => bandLetter(b) === letter)
  const configured = band?.startTime ? Number(band.startTime.split(':')[0]) : NaN
  if (Number.isFinite(configured)) return configured
  return letter === 'N' ? 18 : letter === 'A' ? 14 : 6
}
